// "Not interested" with its toast (web/profile-ui.js): hides the event, and keeps what's needed to take it back, plus
// the "Not into these?" type chips that hide a type from the same toast. Same as ios NotInterested.swift.
import type { NowishEvent } from "../shared/rules";
import { normTag, RETIRED_TAGS, type TagLevel } from "./model";

/** The slice of the profile controller a hide needs. */
export interface HideProfile {
  isStarred(id: string): boolean;
  hideEvent(e: NowishEvent): void;
  unhideEvent(id: string): void;
  setStar(e: NowishEvent, on: boolean): void;
  tagLevel(tag: string): TagLevel | null;
  setTagLevel(tag: string, level: TagLevel | null): void;
}

export class HideSession {
  readonly wasStarred: boolean;
  /** The event's types that can be hidden from the toast. */
  readonly tags: string[];
  /** Types hidden from this toast, and what each was before (hiding replaces an interest), for undo. */
  private readonly before = new Map<string, TagLevel | null>();

  constructor(private readonly profile: HideProfile, readonly event: NowishEvent) {
    this.wasStarred = profile.isStarred(event.id);
    this.tags = [...new Set(event.tags.map(normTag).filter((t) => t && !RETIRED_TAGS.has(t)))];
    profile.hideEvent(event);
  }

  /** Whether a type is hidden now (from this toast or before it). */
  hidden(tag: string) { return this.profile.tagLevel(tag) === "hidden"; }

  /** Tap a type chip: hide the type, or put it back the way it was (a type hidden before the toast just clears). */
  toggleTag(tag: string) {
    if (!this.hidden(tag)) { this.hideTag(tag); return; }
    this.profile.setTagLevel(tag, this.before.get(tag) ?? null);
    this.before.delete(tag);
  }

  /** "All of these". */
  hideAllTags() { for (const t of this.tags) if (!this.hidden(t)) this.hideTag(t); }

  /** Back to how it was: the event unhidden (and starred again if it was), the types hidden here restored. */
  undo() {
    this.profile.unhideEvent(this.event.id);
    for (const [t, level] of this.before) this.profile.setTagLevel(t, level);
    this.before.clear();
    if (this.wasStarred) this.profile.setStar(this.event, true);
  }

  private hideTag(tag: string) {
    this.before.set(tag, this.profile.tagLevel(tag));
    this.profile.setTagLevel(tag, "hidden");
  }
}
